import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { JobsTable } from "./components";
import { Pagination } from "@/components/common/Pagination";
import { PageHeader } from "@/components/common/PageHeader";
import { PageHeaderWithBack } from "@/components/common";
import { Button } from "@/components/common/Button";
import { useAuth } from "@/context/AuthContext";
import { jobsService } from "@/api/services/jobs.service";
import styles from "./Jobs.module.css";
// Status filter options matching backend values
const STATUS_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: "new_project", label: "New Project" },
    { value: "in_progress", label: "In Progress" },
    { value: "scheduled", label: "Scheduled" },
    { value: "on_hold", label: "On Hold" },
    { value: "completed", label: "Completed" },
    { value: "invoiced", label: "Invoiced" },
    { value: "cancelled", label: "Cancelled" },
];
export const Jobs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMountedRef = useRef(true);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Determine base path (admin or staff)
    const basePath = useMemo(() => {
        return location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    }, [location.pathname]);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // Filter state
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const fetchJobs = useCallback(async () => {
        try {
            if (!isMountedRef.current)
                return;
            setLoading(true);
            setError(null);
            const response = await jobsService.getJobs({
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                page: currentPage,
                pageSize: 10,
            });
            // Check if component is still mounted before updating state
            if (!isMountedRef.current)
                return;
            const { data, pagination } = response.data;
            setJobs(data);
            setCurrentPage(pagination.pageNumber);
            setTotalPages(pagination.totalPages);
            setPageSize(pagination.pageSize);
        }
        catch (err) {
            // Ignore errors if component unmounted or request was cancelled
            if (!isMountedRef.current)
                return;
            // Ignore cancellation errors (they're expected when component unmounts)
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            setError("Failed to load jobs");
            console.error("Error fetching jobs:", err);
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [searchTerm, statusFilter, currentPage]);
    useEffect(() => {
        isMountedRef.current = true;
        fetchJobs();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchJobs]);
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            const params = {};
            if (searchTerm)
                params.search = searchTerm;
            if (statusFilter)
                params.status = statusFilter;
            setSearchParams(params);
            setCurrentPage(1);
        }
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleEditClick = (jobId) => {
        navigate(`${basePath}/jobs/${jobId}/edit`);
    };
    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
        setShowFilterMenu(false);
        // Update URL params
        const params = {};
        if (searchTerm)
            params.search = searchTerm;
        if (status)
            params.status = status;
        setSearchParams(params);
    };
    const handleClearFilters = () => {
        setSearchTerm("");
        setStatusFilter("");
        setSearchParams({});
        setCurrentPage(1);
        setShowFilterMenu(false);
    };
    const activeFiltersCount = (statusFilter ? 1 : 0) + (searchTerm ? 1 : 0);
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Jobs", onBack: () => navigate(-1), actions: _jsxs("div", { className: styles.actions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search jobs...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyPress: handleSearch, autoFocus: true })] }), _jsxs("div", { className: styles.filterDropdown, children: [_jsxs("button", { className: styles.filterBtn, onClick: () => setShowFilterMenu(!showFilterMenu), title: "Filter jobs", children: [_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" }) }), activeFiltersCount > 0 && (_jsx("span", { className: styles.filterBadge, children: activeFiltersCount }))] }), showFilterMenu && (_jsxs("div", { className: styles.filterMenu, children: [statusFilter && (_jsx("button", { className: styles.filterOption, onClick: handleClearFilters, children: "Clear Filters" })), STATUS_OPTIONS.filter((opt) => opt.value !== "").map((option) => (_jsx("button", { className: `${styles.filterOption} ${statusFilter === option.value ? styles.activeFilter : ""}`, onClick: () => handleStatusFilter(option.value), children: option.label }, option.value)))] }))] }), _jsx(Button, { className: styles.addJobButton, onClick: () => navigate(`${basePath}/jobs/add`), variant: "secondary", children: "Add Job" })] }) }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading jobs..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchJobs, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && (_jsxs("div", { className: "table-page-listing", children: [_jsx(JobsTable, { jobs: jobs, onEditClick: handleEditClick }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: handlePageChange })] }))] }));
};
